import {
  Check,
  FolderPlus,
  Layers,
  Loader2,
  Pencil,
  Plus,
  Search,
  SlidersHorizontal,
  Target,
  Trash2,
  X,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";

interface SourceGroup {
  name: string;
  sources: string[];
  displayOrder: number;
}

export function DealClassificationTab() {
  const [dealSources, setDealSources] = useState<string[]>([]);
  const [customSources, setCustomSources] = useState<string[]>([]);
  const [nonRelational, setNonRelational] = useState<string[]>([]);
  const [sourceGroups, setSourceGroups] = useState<SourceGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [savingSource, setSavingSource] = useState<string | null>(null);
  const [savedSource, setSavedSource] = useState<string | null>(null);
  const [newSource, setNewSource] = useState("");
  const [addingSource, setAddingSource] = useState(false);

  // Merge deal-derived sources with custom sources (deduplicated, sorted)
  const allSources = useMemo(() => {
    const seen = new Set<string>();
    const merged: string[] = [];
    for (const s of [...dealSources, ...customSources]) {
      const key = s.trim().toLowerCase();
      if (!seen.has(key)) {
        seen.add(key);
        merged.push(s);
      }
    }
    return merged.sort((a, b) => a.localeCompare(b));
  }, [dealSources, customSources]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [sourcesRes, classificationRes, groupsRes, customRes] = await Promise.all([
        fetch("/api/deal-values/sources"),
        fetch("/api/deal-classification/sources"),
        fetch("/api/deal-classification/source-groups"),
        fetch("/api/deal-classification/custom-sources"),
      ]);
      if (sourcesRes.ok) {
        const data = await sourcesRes.json();
        setDealSources(data.values ?? []);
      }
      if (classificationRes.ok) {
        const data = await classificationRes.json();
        setNonRelational(data.sources ?? []);
      }
      if (groupsRes.ok) {
        const data = await groupsRes.json();
        setSourceGroups(data.groups ?? []);
      }
      if (customRes.ok) {
        const data = await customRes.json();
        setCustomSources(data.sources ?? []);
      }
    } catch {
      // silently fail
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const saveGroups = async (groups: SourceGroup[]) => {
    try {
      const res = await fetch("/api/deal-classification/source-groups", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ groups }),
      });
      if (res.ok) {
        setSourceGroups(groups);
        return true;
      }
    } catch {
      // silently fail
    }
    return false;
  };

  const addCustomSource = async () => {
    const trimmed = newSource.trim();
    if (!trimmed) return;
    // Check for duplicates
    if (allSources.some((s) => s.trim().toLowerCase() === trimmed.toLowerCase())) return;

    setAddingSource(true);
    const updated = [...customSources, trimmed];
    try {
      const res = await fetch("/api/deal-classification/custom-sources", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sources: updated }),
      });
      if (res.ok) {
        setCustomSources(updated);
        setNewSource("");
      }
    } catch {
      // silently fail
    } finally {
      setAddingSource(false);
    }
  };

  const toggleSource = async (source: string) => {
    const isCurrentlyNonRelational = nonRelational.some(
      (s) => s.trim().toLowerCase() === source.trim().toLowerCase(),
    );

    const updated = isCurrentlyNonRelational
      ? nonRelational.filter((s) => s.trim().toLowerCase() !== source.trim().toLowerCase())
      : [...nonRelational, source];

    setNonRelational(updated);
    setSavingSource(source);

    try {
      const res = await fetch("/api/deal-classification/sources", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sources: updated }),
      });
      if (res.ok) {
        setSavedSource(source);
        setTimeout(() => setSavedSource(null), 1500);
      } else {
        setNonRelational(
          isCurrentlyNonRelational
            ? [...nonRelational]
            : nonRelational.filter((s) => s.trim().toLowerCase() !== source.trim().toLowerCase()),
        );
      }
    } catch {
      setNonRelational(
        isCurrentlyNonRelational
          ? [...nonRelational]
          : nonRelational.filter((s) => s.trim().toLowerCase() !== source.trim().toLowerCase()),
      );
    } finally {
      setSavingSource(null);
    }
  };

  const isNonRelational = (source: string) =>
    nonRelational.some((s) => s.trim().toLowerCase() === source.trim().toLowerCase());

  const filtered = allSources.filter((s) => s.toLowerCase().includes(search.toLowerCase()));

  const nonRelationalCount = allSources.filter(isNonRelational).length;
  const relationalCount = allSources.length - nonRelationalCount;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand-navy/10">
              <SlidersHorizontal className="h-4 w-4 text-brand-navy" />
            </div>
            <div>
              <CardTitle className="font-serif">Deal Sources</CardTitle>
              <CardDescription>
                Classify deal sources as relational or non-relational. Sources not marked as
                non-relational are treated as relational by default.
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {loading ? (
            <div className="space-y-3">
              <Skeleton className="h-9 w-full" />
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={`skel-${i}`} className="h-12 w-full" />
              ))}
            </div>
          ) : (
            <>
              <form
                className="flex items-center gap-2"
                onSubmit={(e: React.FormEvent<HTMLFormElement>) => {
                  e.preventDefault();
                  addCustomSource();
                }}
              >
                <Input
                  placeholder="Add a new source..."
                  value={newSource}
                  onChange={(e) => setNewSource(e.target.value)}
                  className="flex-1"
                />
                <Button
                  type="submit"
                  size="sm"
                  variant="outline"
                  disabled={!newSource.trim() || addingSource}
                >
                  {addingSource ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <Plus className="h-3.5 w-3.5" />
                  )}
                  <span className="ml-1.5">Add</span>
                </Button>
              </form>

              {allSources.length === 0 ? (
                <p className="py-8 text-center text-sm text-muted-foreground">
                  No deal sources found. Sync deals from HubSpot or add sources manually above.
                </p>
              ) : (
                <>
                  <div className="flex items-center gap-4">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Search sources..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="pl-9"
                      />
                    </div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground shrink-0">
                      <span>{relationalCount} relational</span>
                      <span className="text-border">/</span>
                      <span>{nonRelationalCount} non-relational</span>
                    </div>
                  </div>

                  <div className="divide-y rounded-lg border">
                    {filtered.map((source) => {
                      const isNR = isNonRelational(source);
                      const isSaving = savingSource === source;
                      const justSaved = savedSource === source;

                      return (
                        <button
                          key={source}
                          type="button"
                          onClick={() => toggleSource(source)}
                          disabled={isSaving}
                          className="flex w-full items-center justify-between px-4 py-3 text-left text-sm transition-colors hover:bg-muted/50 disabled:opacity-50"
                        >
                          <span className="font-medium">{source}</span>
                          <div className="flex items-center gap-2">
                            {isSaving && (
                              <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
                            )}
                            {justSaved && <Check className="h-3.5 w-3.5 text-emerald-500" />}
                            <Badge
                              variant={isNR ? "warning" : "secondary"}
                              className="text-[11px] pointer-events-none"
                            >
                              {isNR ? "Non-relational" : "Relational"}
                            </Badge>
                          </div>
                        </button>
                      );
                    })}
                    {filtered.length === 0 && (
                      <p className="px-4 py-6 text-center text-sm text-muted-foreground">
                        No sources match "{search}"
                      </p>
                    )}
                  </div>
                </>
              )}
            </>
          )}
        </CardContent>
      </Card>
      <SourceGroupsCard
        nonRelational={nonRelational}
        sourceGroups={sourceGroups}
        onSave={saveGroups}
      />
      <FPDigitalSourcesCard allSources={allSources} />
    </div>
  );
}

// ── Source Groups Management ──────────────────────────────────────────

function SourceGroupsCard({
  nonRelational,
  sourceGroups,
  onSave,
}: {
  nonRelational: string[];
  sourceGroups: SourceGroup[];
  onSave: (groups: SourceGroup[]) => Promise<boolean>;
}) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);

  // Sources already assigned to a group
  const groupedSources = new Set(
    sourceGroups.flatMap((g) => g.sources.map((s) => s.trim().toLowerCase())),
  );

  // NR sources available for grouping (not already in another group)
  const availableForGrouping = (excludeGroupIndex?: number) =>
    nonRelational.filter((s) => {
      const key = s.trim().toLowerCase();
      if (excludeGroupIndex != null) {
        // When editing, also include sources from the group being edited
        const editGroup = sourceGroups[excludeGroupIndex];
        if (editGroup?.sources.some((gs) => gs.trim().toLowerCase() === key)) return true;
      }
      return !groupedSources.has(key);
    });

  const handleDelete = async (index: number) => {
    setSaving(true);
    const updated = sourceGroups.filter((_, i) => i !== index);
    await onSave(updated.map((g, i) => ({ ...g, displayOrder: i })));
    setSaving(false);
  };

  const handleEdit = (index: number) => {
    setEditingIndex(index);
    setDialogOpen(true);
  };

  const handleAdd = () => {
    setEditingIndex(null);
    setDialogOpen(true);
  };

  const handleDialogSave = async (name: string, sources: string[]) => {
    setSaving(true);
    let updated: SourceGroup[];
    if (editingIndex != null) {
      updated = sourceGroups.map((g, i) => (i === editingIndex ? { ...g, name, sources } : g));
    } else {
      updated = [...sourceGroups, { name, sources, displayOrder: sourceGroups.length }];
    }
    const ok = await onSave(updated.map((g, i) => ({ ...g, displayOrder: i })));
    setSaving(false);
    if (ok) setDialogOpen(false);
  };

  const editGroup = editingIndex != null ? sourceGroups[editingIndex] : null;

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand-navy/10">
                <Layers className="h-4 w-4 text-brand-navy" />
              </div>
              <div>
                <CardTitle className="font-serif">Source Groups</CardTitle>
                <CardDescription>
                  Group non-relational sources together for aggregated reporting on owner
                  dashboards.
                </CardDescription>
              </div>
            </div>
            <Button size="sm" variant="outline" onClick={handleAdd} disabled={saving}>
              <FolderPlus className="h-3.5 w-3.5 mr-1.5" />
              Add Group
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {sourceGroups.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">
              No source groups defined. Sources will appear individually on owner dashboards.
            </p>
          ) : (
            <div className="divide-y rounded-lg border">
              {sourceGroups.map((group, index) => (
                <div key={group.name} className="flex items-center justify-between px-4 py-3">
                  <div className="space-y-1">
                    <span className="text-sm font-medium">{group.name}</span>
                    <div className="flex flex-wrap gap-1">
                      {group.sources.map((s) => (
                        <Badge key={s} variant="secondary" className="text-[11px]">
                          {s}
                        </Badge>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7"
                      onClick={() => handleEdit(index)}
                      disabled={saving}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7 text-destructive hover:text-destructive"
                      onClick={() => handleDelete(index)}
                      disabled={saving}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <SourceGroupDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        availableSources={availableForGrouping(editingIndex ?? undefined)}
        initialName={editGroup?.name ?? ""}
        initialSources={editGroup?.sources ?? []}
        onSave={handleDialogSave}
        saving={saving}
      />
    </>
  );
}

function SourceGroupDialog({
  open,
  onOpenChange,
  availableSources,
  initialName,
  initialSources,
  onSave,
  saving,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  availableSources: string[];
  initialName: string;
  initialSources: string[];
  onSave: (name: string, sources: string[]) => Promise<void>;
  saving: boolean;
}) {
  const [name, setName] = useState(initialName);
  const [selected, setSelected] = useState<string[]>(initialSources);
  const [sourceSearch, setSourceSearch] = useState("");

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setName(initialName);
      setSelected(initialSources);
      setSourceSearch("");
    }
  }, [open, initialName, initialSources]);

  const toggle = (source: string) => {
    setSelected((prev) =>
      prev.some((s) => s.trim().toLowerCase() === source.trim().toLowerCase())
        ? prev.filter((s) => s.trim().toLowerCase() !== source.trim().toLowerCase())
        : [...prev, source],
    );
  };

  const filteredSources = availableSources.filter((s) =>
    s.toLowerCase().includes(sourceSearch.toLowerCase()),
  );

  const canSave = name.trim().length > 0 && selected.length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-serif">
            {initialName ? "Edit Source Group" : "New Source Group"}
          </DialogTitle>
          <DialogDescription>
            Group non-relational sources together. Grouped sources will display as a single row on
            owner dashboards.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="group-name">Group Name</Label>
            <Input
              id="group-name"
              placeholder="e.g., Social Media"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label>Sources ({selected.length} selected)</Label>
            {selected.length > 0 && (
              <div className="flex flex-wrap gap-1 pb-1">
                {selected.map((s) => (
                  <Badge key={s} variant="default" className="text-xs gap-1 pr-1">
                    {s}
                    <button
                      type="button"
                      onClick={() => toggle(s)}
                      className="ml-0.5 rounded-full hover:bg-white/20 p-0.5"
                    >
                      <X className="h-2.5 w-2.5" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                placeholder="Search sources..."
                value={sourceSearch}
                onChange={(e) => setSourceSearch(e.target.value)}
                className="pl-9 h-8 text-sm"
              />
            </div>
            <div className="max-h-48 overflow-y-auto rounded-lg border divide-y">
              {filteredSources.length === 0 ? (
                <p className="px-3 py-4 text-center text-xs text-muted-foreground">
                  {availableSources.length === 0
                    ? "All non-relational sources are already in groups"
                    : "No sources match search"}
                </p>
              ) : (
                filteredSources.map((source) => {
                  const isSelected = selected.some(
                    (s) => s.trim().toLowerCase() === source.trim().toLowerCase(),
                  );
                  return (
                    <button
                      key={source}
                      type="button"
                      onClick={() => toggle(source)}
                      className="flex w-full items-center justify-between px-3 py-2 text-left text-sm transition-colors hover:bg-muted/50"
                    >
                      <span>{source}</span>
                      {isSelected && <Check className="h-3.5 w-3.5 text-emerald-500" />}
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={() => onSave(name.trim(), selected)} disabled={!canSave || saving}>
            {saving && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
            {initialName ? "Save Changes" : "Create Group"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── FP Digital Sources ──────────────────────────────────────────────

function FPDigitalSourcesCard({ allSources }: { allSources: string[] }) {
  const [fpSources, setFpSources] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => {
    fetch("/api/deal-classification/fp-digital-sources")
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data?.sources) setFpSources(data.sources);
      })
      .finally(() => setLoading(false));
  }, []);

  const save = async (sources: string[]) => {
    setSaving(true);
    try {
      const res = await fetch("/api/deal-classification/fp-digital-sources", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sources }),
      });
      if (res.ok) setFpSources(sources);
    } finally {
      setSaving(false);
    }
  };

  const toggle = (source: string) => {
    const key = source.trim().toLowerCase();
    const isSelected = fpSources.some((s) => s.trim().toLowerCase() === key);
    save(
      isSelected ? fpSources.filter((s) => s.trim().toLowerCase() !== key) : [...fpSources, source],
    );
  };

  const available = allSources.filter(
    (s) => !fpSources.some((f) => f.trim().toLowerCase() === s.trim().toLowerCase()),
  );
  const filtered = available.filter((s) => s.toLowerCase().includes(search.toLowerCase()));

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand-navy/10">
            <Target className="h-4 w-4 text-brand-navy" />
          </div>
          <div>
            <CardTitle className="font-serif">FP Digital</CardTitle>
            <CardDescription>
              Deal sources that count as "digital" for FP Reporting metrics. Used by all owners with
              FP goals.
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <Skeleton className="h-24 w-full" />
        ) : (
          <div className="space-y-3">
            {fpSources.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {fpSources.map((source) => (
                  <Badge key={source} variant="default" className="text-xs gap-1 pr-1">
                    {source}
                    <button
                      type="button"
                      onClick={() => toggle(source)}
                      disabled={saving}
                      className="ml-0.5 rounded-full hover:bg-white/20 p-0.5"
                    >
                      <X className="h-2.5 w-2.5" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
            {available.length > 0 && (
              <>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                  <Input
                    placeholder="Search sources to add..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-9 h-8 text-sm"
                  />
                </div>
                <div className="max-h-48 overflow-y-auto rounded-lg border divide-y">
                  {filtered.length === 0 ? (
                    <p className="px-3 py-4 text-center text-xs text-muted-foreground">
                      No sources match search
                    </p>
                  ) : (
                    filtered.map((source) => (
                      <button
                        key={source}
                        type="button"
                        onClick={() => toggle(source)}
                        disabled={saving}
                        className="flex w-full items-center justify-between px-3 py-2 text-left text-sm transition-colors hover:bg-muted/50 disabled:opacity-50"
                      >
                        <span>{source}</span>
                        <Plus className="h-3.5 w-3.5 text-muted-foreground" />
                      </button>
                    ))
                  )}
                </div>
              </>
            )}
            {fpSources.length === 0 && available.length === 0 && (
              <p className="py-4 text-center text-sm text-muted-foreground">
                No deal sources available. Sync deals from HubSpot first.
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
